import React, { FC, PropsWithChildren, useState } from 'react';

import styled from 'styled-components';

import HorizontalScrollableList from '@components/utils/HorizontalScrollableList';
import {
  useGalleriesKeys,
  default as ImageViewer,
} from '@components/galleries/Gallery';
import { Available, AvailableLimited } from '@assets/icons';
import { RoomImageViewModel, RoomViewModel } from '@api/index';
import ListingItemSlider, {
  SliderImage,
} from '@components/ui/listing/ListingItemSlider';
import { Col } from '@components/formStyles';
import { FALLBACK_IMAGE_URL } from '@constants/constants';

import { HotelFeatures, RoomFeatures } from './FeatureSetRenderers';
import {
  Container,
  Content,
  GalleryItem,
  GalleryTitle,
  ImageContainer,
  TopMenu,
  TopMenuItem,
  ModalCloseButton,
  ChevronUp,
  ChevronDown,
  FeatureHeader,
  FeatureContainer,
  Legend,
  LegendItem,
  LegendTitle,
  GalleryContainerMobile,
  RoomFeaturesContainer,
  ModalClose,
  RoomTitle,
} from './DetailsGalleriesModalMobileStyles';
import {
  DetailsGalleriesModalProps,
  ModalView,
  EitherRoomOrHotelFeaturesMeta,
  HotelFeatureGroup,
} from './types';
import SelectVariationTab from './SelectVariationTab';
import { FeaturesBlock } from './styled';

const HorizontalImageList = styled(HorizontalScrollableList)`
  width: 100vw;
`;
let currentIndex: number;

export const formatRoomSliderImage = (
  images?: RoomImageViewModel[]
): SliderImage[] =>
  images?.map((i) => ({
    imageUrl: i.url || '',
    alt: '',
    providerId: i.providerId,
    placeholderUrl: i.placeholderUrl,
  })) || [];

export const Gallery: FC<{
  title: string;
  images: DetailsGalleriesModalProps['galleries'][string];
  showGallery: () => void;
}> = ({ title, images = [], showGallery }) => (
  <GalleryItem>
    <GalleryTitle>
      {title}&nbsp;({images.length})
    </GalleryTitle>
    <HorizontalImageList>
      {images.map((d, index) => (
        <ImageContainer
          key={d.original}
          onClick={() => {
            currentIndex = index;
            showGallery();
          }}
        >
          <img alt={d.originalAlt} src={d.original} loading='lazy' />
        </ImageContainer>
      ))}
    </HorizontalImageList>
  </GalleryItem>
);

type GalleryListProps = Pick<DetailsGalleriesModalProps, 'galleries'> & {
  closeGallery: () => void;
  showGallery: (key: string) => void;
  activeGallery: string;
};
export const GalleryList: FC<GalleryListProps> = ({
  galleries,
  closeGallery,
  activeGallery,
  showGallery,
}) => {
  const keys = useGalleriesKeys(galleries);
  return (
    <>
      {activeGallery ? (
        <GalleryContainerMobile>
          <ImageViewer
            galleries={{ [activeGallery]: galleries[activeGallery] }}
            closeGallery={closeGallery}
            title={activeGallery}
            index={currentIndex || 0}
            hideCategoryAll
          />
        </GalleryContainerMobile>
      ) : (
        <>
          {keys.slice(1).map((title) => (
            <Gallery
              title={title}
              images={galleries[title]}
              key={title}
              showGallery={() => showGallery(title)}
            />
          ))}
        </>
      )}
    </>
  );
};

const FeatureHeaderMobile = ({ children }: PropsWithChildren) => (
  <FeatureHeader>
    {children}
    <ChevronDown />
    <ChevronUp />
  </FeatureHeader>
);

const EntityDisplayTitle = {
  hotel: 'Otel',
  room: 'Oda',
};
const DetailsGalleriesModalMobile: FC<
  Pick<
    DetailsGalleriesModalProps,
    'galleries' | 'closeModal' | 'roomOptionDetailProps'
  > & {
    view: ModalView;
    room?: RoomViewModel;
  } & EitherRoomOrHotelFeaturesMeta
> = ({
  closeModal,
  galleries,
  view: initialView,
  roomOptionDetailProps,
  room,
  ...rest
}) => {
  const [view, setView] = useState<ModalView>(initialView);
  const [activeGallery, setActiveGallery] = useState('');

  const title =
    rest.entityType === 'roomOptions'
      ? `${room?.name}${room?.typeName ? `, ${room?.typeName}` : ''}`
      : EntityDisplayTitle[rest.entityType];

  const isRoomView = rest?.entityType === 'room';

  return (
    <Container activeGallery='noPadding'>
      {activeGallery ? null : (
        <ModalClose>
          <RoomTitle>{title}</RoomTitle>
          <ModalCloseButton
            paletteColor='whiteAlpha600'
            onClick={closeModal}
            noLabel
          />
        </ModalClose>
      )}
      <>
        <>
          {isRoomView ? (
            <TopMenu activeItem={view}>
              <TopMenuItem
                data-item='galleries'
                onClick={() => setView(ModalView.GALLERIES)}
              >
                {EntityDisplayTitle[rest.entityType]} Fotoğrafları
              </TopMenuItem>
              <TopMenuItem
                data-item='details'
                onClick={() => setView(ModalView.DETAILS)}
              >
                {EntityDisplayTitle[rest.entityType]} Özellikleri
              </TopMenuItem>
            </TopMenu>
          ) : null}
          <Content>
            {view === ModalView.GALLERIES ? (
              <GalleryList
                galleries={galleries}
                activeGallery={activeGallery}
                closeGallery={() => setActiveGallery('')}
                showGallery={setActiveGallery}
              />
            ) : (
              <>
                {rest.entityType === 'room' ? (
                  <>
                    <Legend>
                      <LegendItem>
                        <Available />
                        <LegendTitle>Ücretsiz olanaklar</LegendTitle>
                      </LegendItem>
                      <LegendItem>
                        <AvailableLimited />
                        <LegendTitle>Ücretli olanaklar</LegendTitle>
                      </LegendItem>
                    </Legend>

                    <div style={{ width: '100%' }}>
                      <HotelFeatures
                        features={rest.features as HotelFeatureGroup[]}
                        Container={FeatureContainer}
                        Header={FeatureHeaderMobile}
                      />
                    </div>
                  </>
                ) : null}

                {rest.entityType === 'room' ? (
                  <RoomFeaturesContainer>
                    <Legend>
                      <LegendItem>
                        <Available />
                        <LegendTitle>Ücretsiz olanaklar</LegendTitle>
                      </LegendItem>
                      <LegendItem>
                        <AvailableLimited />
                        <LegendTitle>Ücretli olanaklar</LegendTitle>
                      </LegendItem>
                    </Legend>
                    <FeaturesBlock>
                      <RoomFeatures
                        key='room'
                        entityType='room'
                        features={rest.features}
                      />
                    </FeaturesBlock>
                  </RoomFeaturesContainer>
                ) : null}
              </>
            )}
          </Content>
        </>

        {rest.entityType === 'roomOptions' && roomOptionDetailProps ? (
          <Col style={{ width: '100%' }}>
            <ListingItemSlider
              images={formatRoomSliderImage(room?.images)}
              fallBackImage={FALLBACK_IMAGE_URL}
              imageStyle={{
                borderRadius: 0,
                height: 206,
              }}
              lazyLoad='ondemand'
            />

            <SelectVariationTab
              openDatePicker={() => {
                roomOptionDetailProps.openDatePicker();
                closeModal?.();
              }}
              roomOptionDetailProps={roomOptionDetailProps}
              onSelectVariation={(v) => {
                roomOptionDetailProps.handleSelectVariation(v);
                closeModal?.();
              }}
              features={rest.features}
              room={room}
            />
          </Col>
        ) : null}
      </>
    </Container>
  );
};

export default DetailsGalleriesModalMobile;
